/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import { array, shape, node, oneOf } from 'prop-types';
import styled from 'styled-components';
import FlexWrapper from 'TMComponents/FlexWrapper';
import DynamicImg from 'TMComponents/ImageDynamic';
import { breakpoints } from 'TMConstants';
import imageProps from 'TMPropTypes/imageProps';
import Content from 'TMComponents/Content';
import debounce from 'TMUtils/debounce';
import cardBackgroundOption from 'TMComponents/GlobalStyle/cardBackgroundStyles';
import commonOptionsPropType from 'TMPropTypes/commonOptionsProps';

const Background = styled.div`
    position: relative;
    height: 100%;
    padding: 5px; // Fix for a weird issue on the other cards
    overflow: hidden;
`;

const Sections = styled(FlexWrapper)`
    overflow: hidden;
    flex-direction: column;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        flex-direction: row;
        gap: 24px;
    }

    justify-content: center;
    align-items: center;
    align-content: center;
    justify-items: center;
    box-sizing: border-box;
    max-width: 1200px;
    min-height: 332px;

    margin-left: auto;
    margin-right: auto;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        display: flex;
        align-items: center;
        ${({ desktopImagePosition }) => {
            switch (desktopImagePosition) {
                case 'left':
                    return 'flex-direction: row-reverse;';
                default:
                    return 'flex-direction: row;';
            }
        }}
    }
`;

const CardSections = styled(Sections)`
    ${({ theme }) =>
        `
        max-width: 890px;
        border-radius: 32px;
        border: 2px solid ${theme.colorSystem.UIElements[9]};
        background-color: ${theme.colorSystem.primary[3]};
    `}
    ${cardBackgroundOption}
`;

const ContentSection = styled(FlexWrapper)`
    word-break: break-word;
    overflow-wrap: break-word;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        padding: 32px 0px;
        ${({ desktopImagePosition }) => {
            switch (desktopImagePosition) {
                case 'left':
                    return `
                    padding-left: 0px;
                    padding-right: 80px;
                    `;
                default:
                    return `
                    padding-left: 80px;
                    padding-right: 0px;
                    padding-bottom: 80px;
                    `;
            }
        }}
    }
`;
const CardContentSection = styled(ContentSection)`
    padding-top: 32px;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        padding-top: 80px;
    }
`;

const ImageSection = styled(FlexWrapper)`
    align-items: center;
    justify-content: center;
    align-self: flex-end;
    overflow: hidden;
    max-width: 600px;
`;

const CardImageSection = styled(ImageSection)``;

const Image = styled(DynamicImg)`
    flex: 1;
`;

const VariantStyled = {
    plain: {
        Sections,
        ContentSection,
        ImageSection
    },
    card: {
        Sections: CardSections,
        ContentSection: CardContentSection,
        ImageSection: CardImageSection
    }
};

const ImageWithContent = ({
    desktopImage,
    mobileImage,
    desktopImagePosition = 'right',
    content,
    children,
    frameVariant = 'plain',
    commonOptions
}) => {
    const [image, setImage] = useState(mobileImage);
    const [viewportWidth, setViewportWidth] = useState(null);

    useEffect(() => {
        if (!viewportWidth) setViewportWidth(window.innerWidth);
        if (viewportWidth < breakpoints.mobileTablet - 100) {
            setImage(mobileImage);
        } else {
            setImage(desktopImage);
        }
    }, [viewportWidth]);

    useEffect(() => {
        window.addEventListener(
            'resize',
            debounce(() => setViewportWidth(window.innerWidth), 100)
        );
        /* istanbul ignore next */
        return () => {
            window.removeEventListener(
                'resize',
                debounce(() => setViewportWidth(window.innerWidth), 100)
            );
        };
    }, []);
    const isDesktop = viewportWidth >= breakpoints.mobileTablet;

    const SectionsComponent = VariantStyled[frameVariant].Sections;
    const ContentSectionComponent =
        VariantStyled[frameVariant].ContentSection;
    const ImageSectionComponent = VariantStyled[frameVariant].ImageSection;

    return (
        <Background>
            <SectionsComponent
                justify="center"
                desktopImagePosition={desktopImagePosition}
                cardBackgroundColor={commonOptions?.cardBackgroundColor}>
                <ContentSectionComponent
                    desktopImagePosition={desktopImagePosition}>
                    <Content
                        content={content}
                        leftAlign={isDesktop || !!Image}>
                        {children}
                    </Content>
                </ContentSectionComponent>
                <ImageSectionComponent
                    desktopImagePosition={desktopImagePosition}>
                    <DynamicImg imageData={image} />
                </ImageSectionComponent>
            </SectionsComponent>
        </Background>
    );
};

ImageWithContent.propTypes = {
    children: node,
    desktopImage: shape(imageProps).isRequired,
    desktopImagePosition: oneOf(['left', 'right']),
    mobileImage: shape(imageProps),
    content: array.isRequired,
    ...commonOptionsPropType,
    frameVariant: oneOf(['plain', 'card'])
};

export default ImageWithContent;
